import type { UseFormReturn } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useAtom } from "jotai";
import { stepDataStateAtom } from "@/state/formSession";
import { QUERY_KEYS } from "@/constants/queryKeys";
import type { MultiStepFormType, StepConfig } from "@/utils/formConfig";
import { CustomerApi } from "@/api/CustomerApi";
import { FormApi } from "@/api/FormApi";

export function useStepQuery(
  config: StepConfig,
  formId: string | null,
  customerId: string | null,
  form: UseFormReturn<MultiStepFormType>,
) {
  const [stepDataState, setStepDataState] = useAtom(stepDataStateAtom);
  const stepState = stepDataState[config.stepKey];
  const enabled = Boolean(formId && config.stepKey !== "summary" && stepState === "sent");

  const maybeFetch = async <T>(fetcher: () => Promise<T>): Promise<T | null> => {
    try {
      const data = await fetcher();
      setStepDataState(prev => ({ ...prev, [config.stepKey]: "sent" }));
      return data;
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        setStepDataState(prev => ({ ...prev, [config.stepKey]: "missing" }));
        return null;
      }
      throw err;
    }
  };

  return useQuery({
    queryKey: [`${QUERY_KEYS.FORM_STEP}-${config.stepKey}`, formId],
    enabled,
    queryFn: async () => {
      if (!formId) return null;

      switch (config.stepKey) {
        case "step1": {
          const apiData = await maybeFetch(() => FormApi.getStep1EnergyConsumption(formId));
          if (!apiData) return null;
          const mapped = config.mapToForm(apiData);
          form.reset({ ...form.getValues(), step1: mapped });
          return apiData;
        }

        case "step2": {
          const apiData = await maybeFetch(() => FormApi.getStep2OperatorAndTariff(formId));
          if (!apiData) return null;
          const mapped = config.mapToForm(apiData);
          form.reset({ ...form.getValues(), step2: mapped });
          return apiData;
        }

        case "step3": {
          const apiData = await maybeFetch(() => FormApi.getStep3EnergyBilling(formId));
          if (!apiData) return null;
          const mapped = config.mapToForm(apiData);
          form.reset({ ...form.getValues(), step3: mapped });
          return apiData;
        }

        case "step4": {
          const apiData = await maybeFetch(() => FormApi.getStep4ConnectionAndInstallation(formId));
          if (!apiData) return null;
          const mapped = config.mapToForm(apiData);
          form.reset({ ...form.getValues(), step4: mapped });
          return apiData;
        }

        case "step5": {
          if (!customerId) return null;
          const apiData = await maybeFetch(() => CustomerApi.getOne(customerId));
          if (!apiData) return null;
          const mapped = config.mapToForm(apiData);
          form.reset({ ...form.getValues(), step5: mapped });
          return apiData;
        }

        case "summary":
        default:
          return null;
      }
    },
    retry: false,
  });
}
